import React, { useMemo, useState } from "react"
import { FlatList, TouchableOpacity, View } from "react-native"
import BFText, { Body, H3, Small } from "@/components/standard/BFText"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/reusables/ui/tabs"
import { CompCard } from "./CompCard"
import { CompMapView } from "./CompMapView"
import { PropertyImageLightbox } from "./PropertyImageLightbox"
import { Button } from "@/components/reusables/ui/button"
import ListIcon from "phosphor-react-native/src/icons/List"
import MapIcon from "phosphor-react-native/src/icons/MapPin"
import CopyIcon from "phosphor-react-native/src/icons/Copy"

type CompCategory =
  | "selected"
  | "closest"
  | "lowest"
  | "highest"
  | "ownScenario"

interface CompSelectorProps {
  analysis?: any
  viewMode: "list" | "map"
  onViewModeChange: (mode: "list" | "map") => void
  ownScenarioComps: Set<string>
  onOwnScenarioCompsChange: (comps: Set<string>) => void
}

export function CompSelector({
  analysis,
  viewMode,
  onViewModeChange,
  ownScenarioComps,
  onOwnScenarioCompsChange,
}: CompSelectorProps) {
  const [selectedTab, setSelectedTab] = useState("all")
  const [lightboxVisible, setLightboxVisible] = useState(false)
  const [selectedCompId, setSelectedCompId] = useState<string | null>(null)
  const [selectedComp, setSelectedComp] = useState<any>(null)

  const comps = analysis?.comps || []
  const selectedComps = comps.filter((comp: any) => comp?.isSelected)

  // Helper functions for comp categorization
  const calculateDistance = (
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number
  ) => {
    const R = 3959 // Earth's radius in miles
    const dLat = ((lat2 - lat1) * Math.PI) / 180
    const dLng = ((lng2 - lng1) * Math.PI) / 180
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  const getClosestComps = (comps: any[], count: number) => {
    const validComps = comps.filter(
      (comp: any) => comp?.arvcomp?.latitude && comp?.arvcomp?.longitude
    )

    if (!analysis?.property?.latitude || !analysis?.property?.longitude) {
      return validComps.slice(0, count) // fallback to first comps if no property coords
    }

    const propertyLat = parseFloat(analysis.property.latitude as string)
    const propertyLng = parseFloat(analysis.property.longitude as string)

    return validComps
      .map((comp: any) => ({
        ...comp,
        distance: calculateDistance(
          propertyLat,
          propertyLng,
          comp.arvcomp.latitude as number,
          comp.arvcomp.longitude as number
        ),
      }))
      .sort((a: any, b: any) => a.distance - b.distance)
      .slice(0, count)
  }

  const getLowestPriceComps = (comps: any[], count: number) => {
    return comps
      .filter((comp: any) => comp?.arvcomp?.pricePerSqft)
      .sort((a: any, b: any) => a.arvcomp.pricePerSqft - b.arvcomp.pricePerSqft)
      .slice(0, count)
  }

  const getHighestPriceComps = (comps: any[], count: number) => {
    return comps
      .filter((comp: any) => comp?.arvcomp?.pricePerSqft)
      .sort((a: any, b: any) => b.arvcomp.pricePerSqft - a.arvcomp.pricePerSqft)
      .slice(0, count)
  }

  // Calculate comp categories
  const compCategories = useMemo(() => {
    const categories = new Map<string, CompCategory[]>()

    if (!analysis?.comps) return categories

    // Selected comps
    selectedComps.forEach((comp: any) => {
      if (comp?.arvcomp?.id) {
        const existing = categories.get(comp.arvcomp.id as string) || []
        existing.push("selected")
        categories.set(comp.arvcomp.id as string, existing)
      }
    })

    // 3 Closest comps
    const closestComps = getClosestComps(comps as any[], 3)
    closestComps.forEach((comp: any) => {
      if (comp?.arvcomp?.id) {
        const existing = categories.get(comp.arvcomp.id as string) || []
        if (!existing.includes("closest")) {
          existing.push("closest")
        }
        categories.set(comp.arvcomp.id as string, existing)
      }
    })

    // 3 Lowest price comps
    const lowestComps = getLowestPriceComps(comps as any[], 3)
    lowestComps.forEach((comp: any) => {
      if (comp?.arvcomp?.id) {
        const existing = categories.get(comp.arvcomp.id as string) || []
        if (!existing.includes("lowest")) {
          existing.push("lowest")
        }
        categories.set(comp.arvcomp.id as string, existing)
      }
    })

    // 3 Highest price comps
    const highestComps = getHighestPriceComps(comps as any[], 3)
    highestComps.forEach((comp: any) => {
      if (comp?.arvcomp?.id) {
        const existing = categories.get(comp.arvcomp.id as string) || []
        if (!existing.includes("highest")) {
          existing.push("highest")
        }
        categories.set(comp.arvcomp.id as string, existing)
      }
    })

    // Own Scenario comps
    ownScenarioComps.forEach((compId: string) => {
      const existing = categories.get(compId) || []
      if (!existing.includes("ownScenario")) {
        existing.push("ownScenario")
      }
      categories.set(compId, existing)
    })

    return categories
  }, [analysis?.comps, selectedComps, ownScenarioComps])

  const handleToggleOwnScenario = (comp: any) => {
    const compId = comp?.arvcomp?.id as string
    if (!compId) return

    const newSet = new Set(ownScenarioComps)
    if (newSet.has(compId)) {
      newSet.delete(compId)
    } else {
      newSet.add(compId)
    }
    onOwnScenarioCompsChange(newSet)
  }

  const getCategoryNames = (compId: string) => {
    const categories = compCategories.get(compId) || []
    const categoryLabels = categories.map((category) => {
      switch (category) {
        case "selected":
          return "Selected"
        case "closest":
          return "3 Closest"
        case "lowest":
          return "3 Lowest $/sq ft"
        case "highest":
          return "3 Highest $/sq ft"
        case "ownScenario":
          return "Own Scenario"
        default:
          return category
      }
    })
    return categoryLabels.join("; ")
  }

  const handleExportCSV = async () => {
    try {
      // Use TSV (Tab-separated values) for better Google Sheets compatibility
      const header = "Address\tBeds\tBaths\tSq Ft\tPrice\tARV\tScenario Matches"

      // CSV Rows
      const rows = comps
        .map((comp: any) => {
          const arvcomp = comp?.arvcomp
          if (!arvcomp) return null

          // For TSV, we just need to escape tabs and newlines, no need for quotes
          const address = ((arvcomp.fullAddress as string) || "N/A")
            .replace(/\t/g, " ")
            .replace(/\n/g, " ")
          const beds = arvcomp.bedrooms || "N/A"
          const baths = arvcomp.bathrooms || "N/A"
          const sqft = arvcomp.area || "N/A"

          // Parse price from pricing JSON
          let price = "N/A"
          if (arvcomp.pricing) {
            try {
              const pricing = JSON.parse(arvcomp.pricing as string)
              price = pricing.price || "N/A"
            } catch {
              price = "N/A"
            }
          }

          const arv = price // For now, using price as ARV since that's what we're displaying
          const scenarios = getCategoryNames(arvcomp.id as string)
            .replace(/\t/g, " ")
            .replace(/\n/g, " ")

          return `${address}\t${beds}\t${baths}\t${sqft}\t${price}\t${arv}\t${scenarios}`
        })
        .filter(Boolean) // Remove null entries

      const tsvContent = [header, ...rows].join("\n")

      // Copy to clipboard (web only)
      if (navigator?.clipboard) {
        await navigator.clipboard.writeText(tsvContent)
        // You might want to show a toast notification here
        console.log("Comps exported to clipboard as TSV!")
      } else {
        console.log("Clipboard not available")
        console.log(tsvContent) // Fallback: log to console
      }
    } catch (error) {
      console.error("Failed to export CSV:", error)
    }
  }

  const ownScenarioCompsList = useMemo(() => {
    return comps.filter(
      (comp: any) =>
        comp?.arvcomp?.id && ownScenarioComps.has(comp.arvcomp.id as string)
    )
  }, [comps, ownScenarioComps])

  const filteredComps = useMemo(() => {
    if (selectedTab === "selected") {
      return selectedComps
    }
    if (selectedTab === "ownScenario") {
      return ownScenarioCompsList
    }
    return comps
  }, [selectedTab, comps, selectedComps, ownScenarioCompsList])

  const handleImagePress = (comp: any) => {
    // Find the first valid UUID-like ID (length > 10 and not empty)
    const arvCompId = comp.arvcomp?.id

    if (!arvCompId) {
      return
    }

    setSelectedCompId(arvCompId as string)
    setSelectedComp(comp)
    setLightboxVisible(true)
  }

  const renderCompItem = ({ item: comp }: { item: any }) => {
    const categories = comp?.arvcomp?.id
      ? compCategories.get(comp.arvcomp.id as string) || []
      : []
    return (
      <CompCard
        key={comp?.id}
        comp={comp}
        analysis={analysis}
        onImagePress={handleImagePress}
        categories={categories}
        onToggleOwnScenario={handleToggleOwnScenario}
      />
    )
  }

  if (!analysis) {
    return (
      <View className="flex-1 justify-center items-center p-6">
        <Body className="text-center text-muted-foreground">
          No analysis data available
        </Body>
      </View>
    )
  }

  return (
    <View className="flex-1" style={{ flex: 1 }}>
      <Vertical className="gap-4 py-4">
        {/* Header with title and controls */}
        <Horizontal className="items-center justify-between">
          <H3>Comparable Properties</H3>

          <Horizontal className="gap-2">
            {/* Export CSV Button */}
            <Button
              variant="outline"
              size="sm"
              onPress={() => {
                void handleExportCSV()
              }}
              testID="export-csv-button"
            >
              <Horizontal className="items-center gap-1">
                <CopyIcon size={16} color="#676080" />
                <BFText>
                  <Small>Copy Table</Small>
                </BFText>
              </Horizontal>
            </Button>

            {/* View Toggle Buttons */}
            <TouchableOpacity
              testID="list-view-toggle"
              onPress={() => {
                onViewModeChange("list")
              }}
              className={`p-2 rounded-md ${
                viewMode === "list" ? "bg-primary" : "bg-secondary"
              }`}
            >
              <ListIcon
                size={20}
                color={viewMode === "list" ? "white" : "#676080"}
              />
            </TouchableOpacity>

            <TouchableOpacity
              testID="map-view-toggle"
              onPress={() => {
                onViewModeChange("map")
              }}
              className={`p-2 rounded-md ${
                viewMode === "map" ? "bg-primary" : "bg-secondary"
              }`}
            >
              <MapIcon
                size={20}
                color={viewMode === "map" ? "white" : "#676080"}
              />
            </TouchableOpacity>
          </Horizontal>
        </Horizontal>

        {/* Tabs */}
        <Tabs
          value={selectedTab}
          onValueChange={setSelectedTab}
          className="flex-1"
        >
          <TabsList className="grid w-full grid-cols-3 flex-row my-2">
            <TabsTrigger value="all">
              <BFText>All ({comps.length})</BFText>
            </TabsTrigger>
            <TabsTrigger value="selected">
              <BFText>Selected ({selectedComps.length})</BFText>
            </TabsTrigger>
            <TabsTrigger value="ownScenario">
              <BFText>Own ({ownScenarioCompsList.length})</BFText>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="flex-1">
            {viewMode === "list" ? (
              <FlatList
                data={filteredComps}
                renderItem={renderCompItem}
                keyExtractor={(item) => (item?.id as string) || ""}
                showsVerticalScrollIndicator={false}
                style={{ flex: 1 }}
                contentContainerStyle={{ gap: 12, paddingBottom: 20 }}
              />
            ) : (
              <CompMapView comps={filteredComps} analysis={analysis} />
            )}
          </TabsContent>

          <TabsContent value="selected" className="flex-1">
            {selectedComps.length === 0 ? (
              <View className="flex-1 justify-center items-center p-6">
                <Body className="text-center text-muted-foreground">
                  No comps selected yet
                </Body>
                <Small className="text-center text-muted-foreground mt-2">
                  Switch to &quot;All Comps&quot; to select comparable
                  properties
                </Small>
              </View>
            ) : viewMode === "list" ? (
              <FlatList
                data={filteredComps}
                renderItem={renderCompItem}
                keyExtractor={(item) => (item?.id as string) || ""}
                showsVerticalScrollIndicator={false}
                style={{ flex: 1 }}
                contentContainerStyle={{ gap: 12, paddingBottom: 20 }}
              />
            ) : (
              <CompMapView comps={filteredComps} analysis={analysis} />
            )}
          </TabsContent>

          <TabsContent value="ownScenario" className="flex-1">
            {ownScenarioCompsList.length === 0 ? (
              <View className="flex-1 justify-center items-center p-6">
                <Body className="text-center text-muted-foreground">
                  No comps in Own Scenario yet
                </Body>
                <Small className="text-center text-muted-foreground mt-2">
                  Use the star button on comp cards to add them to your Own
                  Scenario
                </Small>
              </View>
            ) : viewMode === "list" ? (
              <FlatList
                data={filteredComps}
                renderItem={renderCompItem}
                keyExtractor={(item) => (item?.id as string) || ""}
                showsVerticalScrollIndicator={false}
                style={{ flex: 1 }}
                contentContainerStyle={{ gap: 12, paddingBottom: 20 }}
              />
            ) : (
              <CompMapView comps={filteredComps} analysis={analysis} />
            )}
          </TabsContent>
        </Tabs>

        {/* Image Lightbox */}
        <PropertyImageLightbox
          visible={lightboxVisible}
          onClose={() => {
            setLightboxVisible(false)
            setSelectedCompId(null)
            setSelectedComp(null)
          }}
          analysisId={analysis?.id}
          compId={selectedCompId || undefined}
          fallbackProperty={selectedComp?.arvcomp || {}}
        />
      </Vertical>
    </View>
  )
}
